import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export type VariantEntry = { avif?: string | null; webp?: string | null };
export type VariantMap = Map<string, VariantEntry>;

/**
 * Fetches processed image variants once and caches them in memory.
 * Cached indefinitely — SmartImage components share the same Map.
 */
export function useImageVariants() {
  return useQuery<VariantMap>({
    queryKey: ["image-variants-map"],
    staleTime: Infinity,
    gcTime: Infinity,
    queryFn: async () => {
      const map: VariantMap = new Map();
      // page through — table can grow large
      const pageSize = 1000;
      let from = 0;
      // safety cap
      for (let i = 0; i < 50; i++) {
        const { data, error } = await supabase
          .from("image_variants")
          .select("original_url, avif_url, webp_url")
          .not("processed_at", "is", null)
          .range(from, from + pageSize - 1);
        if (error) throw error;
        if (!data || data.length === 0) break;
        for (const row of data) {
          if (row.avif_url || row.webp_url) {
            map.set(row.original_url, { avif: row.avif_url, webp: row.webp_url });
          }
        }
        if (data.length < pageSize) break;
        from += pageSize;
      }
      return map;
    },
  });
}

export function pickVariants(map: VariantMap | undefined, src: string | undefined | null): VariantEntry {
  if (!src || !map) return {};
  return map.get(src) ?? {};
}
